// Cornerstone "authority" content for the CaraLoom Resources hub.
// Static, in-code, brand-voiced long-form guides rendered as Markdown.
// Each article is genuinely useful and SEO-targeted at high-intent families.

const img = (id, w = 1200, h = 675) =>
  `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&w=${w}&h=${h}&fit=crop`;

const AUTHOR = { name: 'The CaraLoom Care Team', role: 'Reviewed by licensed RNs' };

export const ARTICLES = [
  {
    slug: 'hiring-a-private-duty-nurse-checklist',
    category: 'Hiring Care',
    title: 'The Complete Checklist for Hiring a Private-Duty Nurse',
    metaTitle: 'How to Hire a Private-Duty Nurse: The Complete Family Checklist | CaraLoom',
    metaDescription:
      'A step-by-step checklist for hiring a private-duty nurse for a loved one at home — what to verify, what to ask, red flags to avoid, and how to confirm licensure and insurance.',
    keywords: ['hiring a private duty nurse', 'private duty nurse checklist', 'questions to ask a home nurse', 'how to find a private nurse'],
    excerpt:
      'Bringing a nurse into your home is a big decision. Use this checklist to verify credentials, ask the right questions, and choose with confidence.',
    heroImage: img('photo-1666887360742-974c8fce8e6b'),
    heroAlt: 'A friendly private-duty nurse in scrubs ready to provide in-home care',
    readMinutes: 8,
    datePublished: '2026-06-01',
    dateModified: '2026-06-06',
    tags: ['Private duty nursing', 'Vetting', 'Family guide'],
    related: ['hospital-to-home-discharge-checklist', 'paying-for-home-care', 'preventing-caregiver-burnout'],
    faqs: [
      { q: 'What is the difference between a private-duty nurse and a home health aide?', a: 'A private-duty nurse (RN or LPN) provides clinical care — medications, wound care, IV therapy, assessments. A home health aide (CNA/HHA) provides personal, non-medical care like bathing, dressing, and companionship. Many families use both.' },
      { q: 'How do I verify a nurse’s license?', a: 'Ask for the license number and state, then confirm it on the state board of nursing’s public lookup (or NPI registry). On CaraLoom, every clinician’s license is verified at the source and continuously monitored.' },
      { q: 'How quickly can a private-duty nurse start?', a: 'With CaraLoom, families are often matched within minutes and care can begin within 24–48 hours depending on local availability.' },
    ],
    body: `Hiring a private-duty nurse is one of the most important decisions a family can make. You are inviting a clinician into your home and trusting them with someone you love. The good news: with a clear process, you can choose confidently. Use the checklist below.

## 1. Define exactly what care is needed

Before you talk to anyone, write down the specifics:

- **Diagnosis and clinical needs** — wound care, medication management, IV therapy, tracheostomy/ventilator, diabetes, post-surgical recovery.
- **Hours and schedule** — a few hours a day, overnight, or 24/7.
- **Personal care needs** — help with bathing, mobility, meals, transfers.
- **Must-haves** — language, gender preference, experience with a specific condition (e.g., dementia).

A clear care picture is what lets a good match happen quickly — and it's exactly what CaraLoom's matching engine uses to surface the right clinicians.

## 2. Verify licensure and credentials

Never skip this step.

- Confirm the **RN or LPN license** is active and unrestricted in your state.
- Check for relevant **certifications** (e.g., wound care, hospice/palliative, ACLS).
- Confirm **malpractice/liability coverage**.
- Ask about **specialty experience** that matches your loved one's diagnosis.

> On CaraLoom, identity, license, and background checks are completed and **continuously monitored** before a clinician ever appears in your matches — so the verification work is already done for you.

## 3. Run a proper background check

A clean record is non-negotiable. A thorough screen includes national and county criminal databases, the sex-offender registry, and healthcare-sanctions (OIG/GSA) exclusion lists. Confirm screening was completed recently — not years ago.

## 4. Ask the right interview questions

When you speak with a candidate, ask:

1. Tell me about your experience with [your loved one's condition].
2. How do you handle an emergency or a sudden change in condition?
3. How do you communicate with families and physicians?
4. What does a typical shift look like for you?
5. Can you provide references from families you've cared for?

Listen for **specifics and calm confidence**, not vague reassurance.

## 5. Confirm the practical details in writing

- **Rate and what's included** (transparent, no hidden agency markups).
- **Schedule, start date, and coverage** for missed shifts.
- **Scope of duties** — clinical vs. personal care.
- **Payment terms** — verified-settlement means you only pay for care delivered.

## 6. Watch for red flags

🚩 Reluctance to share a license number · 🚩 No references · 🚩 Pressure to pay cash up front · 🚩 Vague answers about emergencies · 🚩 No clear plan for missed shifts.

## The shortcut

Vetting a nurse the traditional way can take weeks of phone calls. CaraLoom compresses it: you describe the need, review **verified, background-checked** clinicians with transparent rates and real reviews, message before you commit, and book with verified-settlement payments. The checklist above is essentially built into the platform.

**Bottom line:** a great private-duty nurse changes everything. Take the time to verify, ask, and confirm — and lean on tools that do the heavy lifting for you.`,
  },

  {
    slug: 'goals-of-care-family-guide',
    category: 'Care Planning',
    title: 'Goals of Care: What Every Family Needs to Know',
    metaTitle: 'Goals of Care Explained: A Family Guide to Care Decisions | CaraLoom',
    metaDescription:
      'Understand goals-of-care conversations: what they are, who should be involved, the questions to ask, and how to align care with what matters most to your loved one.',
    keywords: ['goals of care', 'goals of care conversation', 'advance care planning', 'care decisions for elderly parents'],
    excerpt:
      'A goals-of-care conversation aligns medical care with what truly matters to your loved one. Here’s how to approach it with clarity and compassion.',
    heroImage: img('photo-1581056771107-24ca5f033842'),
    heroAlt: 'A clinician discussing care goals with a patient and family',
    readMinutes: 7,
    datePublished: '2026-06-02',
    dateModified: '2026-06-06',
    tags: ['Goals of care', 'Advance planning', 'Family guide'],
    related: ['hiring-a-private-duty-nurse-checklist', 'preventing-caregiver-burnout', 'hospital-to-home-discharge-checklist'],
    faqs: [
      { q: 'What does “goals of care” actually mean?', a: 'It’s the shared understanding of what a person wants their medical care to achieve — for example, living independently as long as possible, prioritizing comfort, or pursuing every treatment option. Care decisions then flow from those goals.' },
      { q: 'When should we have this conversation?', a: 'Earlier than you think. The best time is before a crisis — at a new diagnosis, after a hospitalization, or any time health is changing. It can and should be revisited as circumstances change.' },
      { q: 'Who should be involved?', a: 'The person receiving care (whenever possible), key family members, and the clinical team. A designated healthcare proxy should be named so wishes are honored if the person can’t speak for themselves.' },
    ],
    body: `When a loved one is living with serious or advancing illness, families face a stream of medical decisions. A **goals-of-care conversation** is the compass that makes those decisions clearer. It answers one question: *given everything we know, what do we want care to accomplish?*

## Why goals of care matter

Without a shared goal, care can drift toward "doing everything" by default — even when that isn't what the person would choose. With a clear goal, every decision (a hospital transfer, a new medication, a therapy) can be measured against it: *does this move us toward what matters most?*

Goals generally fall along a spectrum:

- **Live longer** — pursue treatments that extend life, even with trade-offs.
- **Maintain function and independence** — prioritize quality of life and staying at home.
- **Maximize comfort** — focus on relief from pain and symptoms.

Most people's goals are a blend, and they evolve over time.

## How to start the conversation

These talks are emotional. A gentle, structured approach helps:

1. **Pick a calm moment** — not in the middle of a crisis if you can avoid it.
2. **Ask permission** — "Would it be okay to talk about what you'd want if your health changes?"
3. **Listen more than you talk** — your job is to understand, not to steer.
4. **Use plain language** — avoid medical jargon.

## Questions worth asking

- What does a good day look like for you right now?
- What abilities or activities matter most to you?
- If you became more ill, what would you be willing — or not willing — to go through?
- Where do you want to receive care if possible? (Many people say *home*.)
- Who should make decisions for you if you can't?

## Turn the conversation into a plan

Talking is the start; documenting protects the wishes:

- Name a **healthcare proxy** (durable power of attorney for healthcare).
- Complete an **advance directive** or living will.
- Ask your clinician about **POLST/MOLST** forms if appropriate.
- Share the documents with everyone involved — including in-home clinicians.

> When care happens at home, your CaraLoom clinician becomes a key partner in honoring these goals — coordinating with your physician, watching for changes, and keeping the plan centered on what your loved one wants.

## Revisit as things change

Goals of care are not a one-time event. After a hospitalization, a new diagnosis, or a noticeable decline, sit down again. The aim is always the same: care that fits the person, not just the disease.

**Bottom line:** a goals-of-care conversation is one of the most loving things a family can do. It replaces guesswork with clarity — and ensures the care your loved one receives is the care they actually want.`,
  },

  {
    slug: 'preventing-caregiver-burnout',
    category: 'Caregiver Support',
    title: 'Caregiver Support & Training: How to Prevent Burnout at Home',
    metaTitle: 'Preventing Caregiver Burnout: Support & Training for Family Caregivers | CaraLoom',
    metaDescription:
      'Family caregiver burnout is real and preventable. Learn the warning signs, practical training tips, and how respite and professional support protect your wellbeing.',
    keywords: ['caregiver burnout', 'family caregiver support', 'caregiver respite', 'caregiver training', 'preventing caregiver stress'],
    excerpt:
      'You can’t pour from an empty cup. Here are the warning signs of caregiver burnout — and concrete ways to protect your health while caring for someone else.',
    heroImage: img('photo-1576765608535-5f04d1e3f289'),
    heroAlt: 'A caregiver providing warm, attentive support to an older adult at home',
    readMinutes: 7,
    datePublished: '2026-06-03',
    dateModified: '2026-06-06',
    tags: ['Caregiver wellbeing', 'Respite care', 'Training'],
    related: ['hiring-a-private-duty-nurse-checklist', 'goals-of-care-family-guide', 'paying-for-home-care'],
    faqs: [
      { q: 'What are the warning signs of caregiver burnout?', a: 'Persistent exhaustion, irritability, withdrawal from friends, trouble sleeping, frequent illness, feeling hopeless or resentful, and neglecting your own health. If these sound familiar, it’s time to get support.' },
      { q: 'What is respite care?', a: 'Respite care is short-term professional care that gives a family caregiver a break — a few hours a week, an overnight, or longer. It’s one of the most effective ways to prevent burnout.' },
      { q: 'How can CaraLoom help family caregivers?', a: 'CaraLoom connects you with verified caregivers and nurses for respite, overnight, or ongoing support, so you can rest, work, and recharge while your loved one stays safely cared for.' },
    ],
    body: `Family caregivers are the backbone of home care — and too often, they run themselves into the ground. Caregiver burnout isn't a sign of weakness; it's the predictable result of carrying too much for too long. The encouraging part: it's preventable.

## Know the warning signs

Burnout creeps in slowly. Watch for:

- Constant **fatigue** that rest doesn't fix
- **Irritability** or feeling resentful toward the person you care for
- **Withdrawing** from friends, hobbies, and yourself
- **Sleep problems**, frequent colds, or new aches
- A sense of **hopelessness** or "I can't keep doing this"

If several of these ring true, treat it as a signal — not a personal failing.

## Build skills with simple training

Confidence reduces stress. A little training goes a long way:

- **Safe transfers and mobility** — protect your back and your loved one from falls.
- **Medication routines** — use a pill organizer and a simple log.
- **Recognizing red flags** — know which changes warrant a call to the clinician.
- **Communication techniques** — especially for dementia (redirection, calm tone, simple choices).

Your in-home nurse or aide can teach you these techniques directly — ask them to show you.

## Protect your own health (this is not optional)

> You cannot pour from an empty cup. Caring for yourself **is** caring for them.

- Keep your **own medical appointments**.
- Guard **sleep** like it's medicine — because it is.
- Move your body and eat real meals, even when it's hard.
- Stay **connected** — isolation fuels burnout.

## Use respite — early and often

Respite care is short-term professional coverage so you can step away. A few hours a week to run errands, an overnight to actually sleep, or a longer break to travel — all of it helps you keep going sustainably.

Bringing in a professional caregiver isn't "giving up." It's building a **team** so the weight isn't on one person.

## Ask for and accept help

Make a specific list of tasks others can own — groceries, rides to appointments, a weekly check-in call — and say yes when people offer. Lean on local support groups and your care team.

> CaraLoom makes it easy to add verified, background-checked support — for a single respite shift or ongoing help — so family caregivers can rest and recharge without worry.

**Bottom line:** the best caregiver is a supported caregiver. Watch for the signs, build a few skills, protect your health, and don't try to do it alone. Your loved one needs you well.`,
  },

  {
    slug: 'paying-for-home-care',
    category: 'Costs & Insurance',
    title: 'Paying for Home Care: Medicare, Insurance & Out-of-Pocket Explained',
    metaTitle: 'How to Pay for Home Care: Medicare, Insurance & Costs Explained | CaraLoom',
    metaDescription:
      'Confused about paying for home care? Understand what Medicare, Medicaid, and private insurance cover, typical hourly costs, and smart ways to fund in-home care.',
    keywords: ['how to pay for home care', 'does medicare cover home care', 'cost of home health care', 'home care insurance coverage'],
    excerpt:
      'Home care costs don’t have to be a mystery. Here’s a clear breakdown of what Medicare, Medicaid, and insurance cover — and how families actually pay.',
    heroImage: img('photo-1454165804606-c3d57bc86b40'),
    heroAlt: 'A family reviewing home-care costs and financial options together',
    readMinutes: 8,
    datePublished: '2026-06-04',
    dateModified: '2026-06-06',
    tags: ['Costs', 'Insurance', 'Medicare'],
    related: ['hiring-a-private-duty-nurse-checklist', 'hospital-to-home-discharge-checklist', 'goals-of-care-family-guide'],
    faqs: [
      { q: 'Does Medicare pay for home care?', a: 'Traditional Medicare may cover intermittent skilled home health (nursing or therapy) for eligible, homebound patients under a physician’s plan of care. It generally does not cover long-term personal/custodial care or continuous private-duty nursing.' },
      { q: 'How much does home care cost per hour?', a: 'It varies by role and region: caregivers/aides commonly run $20–$40/hour, LPNs roughly $40–$65, and RNs $50–$90+. CaraLoom shows transparent rates up front with no hidden agency markups.' },
      { q: 'What if we’re paying out of pocket?', a: 'Many families blend funding: long-term care insurance, VA benefits, Medicaid waivers, HSAs, and personal funds. Booking directly (no agency markup) and matching the right license level to the need keeps costs down.' },
    ],
    body: `One of the first questions families ask is also the most stressful: *how will we pay for this?* The answer depends on the **type of care** and **who's providing the coverage**. Here's a clear map.

## First, know what kind of care you need

Funding rules hinge on this distinction:

- **Skilled care** — clinical work by a licensed nurse or therapist (wound care, IV therapy, rehab). More likely to have insurance coverage.
- **Personal / custodial care** — help with bathing, dressing, meals, companionship. Rarely covered by Medicare; often paid privately or through specific programs.

## Medicare

Traditional **Medicare** may cover **intermittent skilled home health** when a person is *homebound* and a physician orders a plan of care. That can include part-time skilled nursing, physical/occupational/speech therapy, and a home health aide tied to skilled care.

What Medicare generally **does not** cover: 24/7 care, long-term custodial/personal care, or continuous private-duty nursing. **Medicare Advantage** plans sometimes add supplemental in-home benefits — check your specific plan.

## Medicaid

**Medicaid** (and Home & Community-Based Services *waivers*) can cover personal care and in-home support for those who qualify financially. Benefits vary significantly by state, so check your state's program.

## Private & long-term care insurance

- **Long-term care (LTC) insurance** is often the strongest source of funding for ongoing personal care — review the policy's daily benefit, elimination period, and what triggers coverage.
- **Private health insurance** may cover skilled, medically necessary home health similar to Medicare.
- **VA benefits** (including Aid & Attendance) can help eligible veterans and spouses.

## Typical out-of-pocket costs

When paying privately, expect roughly:

- **Caregivers / home health aides:** ~$20–$40 / hour
- **LPNs:** ~$40–$65 / hour
- **RNs:** ~$50–$90+ / hour

Rates vary by region, acuity, and shift type (overnight or 24/7 differ).

## Smart ways to lower the cost

1. **Match the license level to the need.** Don't pay RN rates for companionship; don't ask an aide to do skilled tasks.
2. **Cut the middleman.** Traditional agencies add markups. Booking **directly** with a clinician keeps more of your money on care.
3. **Blend funding sources.** Many families combine insurance, benefits, HSAs, and personal funds.
4. **Use transparent pricing.** Know the rate before you commit.

> CaraLoom shows **transparent, upfront rates** and lets you add your insurance so you can see which clinicians are **in-network** — no surprise bills, no hidden agency premiums.

**Bottom line:** paying for home care is rarely a single source — it's usually a smart blend. Start by identifying the type of care, check Medicare/Medicaid/insurance eligibility, and use transparent, direct booking to make every dollar go further.

*This article is general information, not financial or legal advice. Confirm coverage details with your plan and a qualified advisor.*`,
  },

  {
    slug: 'hospital-to-home-discharge-checklist',
    category: 'Recovery',
    title: 'Hospital-to-Home: The Discharge Checklist That Prevents Readmission',
    metaTitle: 'Hospital Discharge Checklist: Prevent Readmission & Recover Safely at Home | CaraLoom',
    metaDescription:
      'The days after discharge carry the highest readmission risk. Use this hospital-to-home checklist to set up medications, follow-ups, and in-home support for a safe recovery.',
    keywords: ['hospital discharge checklist', 'prevent hospital readmission', 'post hospital care at home', 'discharge planning'],
    excerpt:
      'Nearly 1 in 5 patients is readmitted within 30 days — often preventably. This checklist helps families set up a safe, confident transition home.',
    heroImage: img('photo-1538108149393-fbbd81895907'),
    heroAlt: 'A hospital setting representing the transition from hospital to home',
    readMinutes: 8,
    datePublished: '2026-06-05',
    dateModified: '2026-06-06',
    tags: ['Discharge planning', 'Post-hospital care', 'Readmission'],
    related: ['hiring-a-private-duty-nurse-checklist', 'paying-for-home-care', 'goals-of-care-family-guide'],
    faqs: [
      { q: 'Why is the period right after discharge so risky?', a: 'The first 72 hours carry the highest risk of complications and readmission — usually from medication errors, missed follow-ups, or unrecognized warning signs. Skilled in-home support during this window dramatically improves outcomes.' },
      { q: 'What should be arranged before discharge?', a: 'Medication reconciliation, follow-up appointments, equipment, a clear list of warning signs, and in-home help if needed. Ideally, arrange care so support is waiting when your loved one gets home.' },
      { q: 'Can CaraLoom arrange care fast for a discharge?', a: 'Yes. Tell us the discharge date and care needs and we surface available, verified clinicians who can often start within 24–48 hours — many families set up same-day coverage.' },
    ],
    body: `The transition from hospital to home is one of the most fragile moments in a person's care. Nearly **one in five** Medicare patients is readmitted within 30 days — and many of those readmissions are preventable. A calm, organized discharge plan is your best protection.

## Before discharge: ask and confirm

Don't leave the hospital without clear answers:

- **Diagnosis & recovery plan** — what happened and what recovery should look like.
- **Medication reconciliation** — an updated list of every medication, dose, and timing. Ask which old meds to stop.
- **Warning signs** — exactly which symptoms mean "call the doctor" vs. "call 911."
- **Follow-up appointments** — booked before you leave, not "sometime soon."
- **Equipment & supplies** — walker, oxygen, wound supplies, etc., arranged for the home.
- **Who to call** — a name and number for questions after hours.

## Prepare the home

- Clear pathways and remove trip hazards (rugs, cords).
- Set up a **safe sleeping and bathing area** — grab bars, a shower chair if needed.
- Organize medications with a pill organizer and a written schedule.
- Stock easy meals and keep water within reach.

## The first 72 hours: the highest-risk window

This is where complications and readmissions cluster. Build a tight plan:

1. **Fill prescriptions immediately** — before symptoms can flare.
2. **Start the medication schedule** correctly from day one.
3. **Watch for red flags** — fever, worsening pain, breathing changes, confusion, wound issues.
4. **Keep the first follow-up** — even if your loved one "feels fine."

> This is precisely when an in-home nurse earns their keep: medication management, wound care, vital-sign monitoring, and catching small problems before they become emergencies.

## Consider professional in-home support

For higher-risk recoveries — surgery, heart failure, complex medications, or a frail patient — skilled support at home is proven to reduce avoidable readmissions. Even a few visits in the first weeks can change the trajectory.

**A blended team often works best:** a recovery nurse for the clinical work, plus a caregiver for daily living and safety.

## How CaraLoom helps discharge transitions

Time is short when a discharge is approaching. CaraLoom lets you:

- Describe the **diagnosis and discharge date** and instantly see **verified, available** clinicians.
- Compare **transparent rates** and real reviews.
- **Book quickly** — many families arrange coverage within 24–48 hours, so help is waiting at the door.

**Bottom line:** a safe recovery is planned, not hoped for. Confirm the essentials before discharge, prepare the home, lock down the first 72 hours, and bring in skilled support when the recovery warrants it.`,
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
export const ARTICLE_SLUGS = ARTICLES.map((a) => a.slug);
export const ARTICLE_CATEGORIES = [...new Set(ARTICLES.map((a) => a.category))];
